import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Script from 'next/script';
import { Abril_Fatface as AbrilFatface, Open_Sans as OpenSans } from 'next/font/google';
import {
  positions,
  AlertComponentPropsWithStyle,
  Provider as AlertProvider,
  transitions,
} from 'react-alert';
import { ApolloProvider } from '@apollo/client';
import { useApollo } from '@firsttable/apollo-client';
import WebTheme from '@firsttable/web-theme/layout';
import Box from '@firsttable/web-components/atoms/Box';
import Alert from '@firsttable/web-components/atoms/Alert';
import { AuthProvider } from '@context/AuthContext';
import { ModalProvider } from '@context/ModalContext';
import { SignupAttributionProvider } from '@context/SignupAttribution';
import '@/components/organisms/Forms/validationExtensions';
import {
  AnalyticsProvider,
  GrowthBookProvider,
} from '@/components/analytics/context/AnalyticsContext';
import AnalyticsAuthBridge from '@/components/analytics/AnalyticsAuthBridge';
import ErrorBoundary from '@/components/organisms/ErrorBoundary/ErrorBoundary';
import useReactErrorTelemetry from '@/lib/newrelic/useReactErrorTelemetry';
import NewRelicErrorContextProvider from '@/lib/newrelic/context/NewRelicErrorContextProvider';
import { GlobalConsentBanner } from '@/components/analytics/context/Consent';
import AmplitudeExperimentScript from '@/components/analytics/AmplitudeExperimentScript';
import GtmScript from '@/components/analytics/GtmScript';
import SnapchatScript from '@/components/analytics/SnapchatScript';
import MetaPixelScript from '@/components/analytics/MetaPixelScript';
import OpenAIAdsScript from '@/components/analytics/OpenAIAdsScript';
import PodscribeScript from '@/components/analytics/PodscribeScript';
import AdalyserScript from '@/components/analytics/AdalyserScript';
import HotjarScript from '@/components/analytics/HotjarScript';
import HubSpotConsent from '@/components/analytics/HubSpotConsent';
import SnapchatPixelUpdater from '@/components/analytics/SnapchatPixelUpdater';
import MetaPixelUpdater from '@/components/analytics/MetaPixelUpdater';
import BrazeInitializer from '@/components/analytics/BrazeInitializer';
import globalState from '@/lib/globalState';
import {
  NEXT_PUBLIC_APOLLO_ORIGIN,
  NEXT_PUBLIC_GRAPHQL_BUILD_ENDPOINT,
  NEXT_PUBLIC_GRAPHQL_ENDPOINT,
  NEXT_PUBLIC_GRAPHQL_MUTATION_ENDPOINT,
} from '@/lib/env';
import { logoutUser } from '@/lib/apollo';
import 'react-lazy-load-image-component/src/effects/opacity.css';
// Can remove the following three when externalDir in next.config.js supports scss/css
import '@firsttable/web-components/organisms/Swiper/scss/swiper-core-5.1.0.scss'; // For LightBoxGallery on Restaurant
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/mousewheel';
import 'swiper/css/keyboard';
import 'swiper/css/pagination';
import '../styles/tailwind.css';
import '../styles/scss/base.scss';
import '../styles/scss/modal.scss';
import '../styles/datepicker.css';
import '../styles/map.css';
import '../styles/scss/photoswipe/main.scss';
import '../styles/scss/photoswipe/default-skin/default-skin.scss';

const alertTemplateDefaultOptions = {};
const AlertTemplate: React.ComponentType<AlertComponentPropsWithStyle> = ({
  options = alertTemplateDefaultOptions,
  close,
  message,
  ...props
}: {
  options?: any;
  close: () => void;
  message?: React.ReactNode;
}) =>
  options?.unwrapped ? (
    message
  ) : (
    <ModalProvider>
      <Box m={15} {...options.containerStyle}>
        <Alert options={options} close={options.close || close} message={message} {...props} />
      </Box>
    </ModalProvider>
  );

AlertTemplate.propTypes = {
  options: PropTypes.any,
  close: PropTypes.func.isRequired,
};

const alertOptions = {
  offset: '25px',
  timeout: 3000,
  transition: transitions.SCALE,
  containerStyle: {
    zIndex: 9999, // need to bump this up to stack above modals (999)
  },
};

const openSans = OpenSans({
  subsets: ['latin'],
  variable: '--font-open-sans',
  display: 'swap',
});

const abrilFatface = AbrilFatface({
  weight: ['400'],
  subsets: ['latin'],
  variable: '--font-abril-fatface',
  display: 'swap',
});

const App = ({ Component, pageProps }: { Component: any; pageProps: any }) => {
  useReactErrorTelemetry();

  // Apply a custom stack trace limit on mount (after New Relic agent, which loads async).
  useEffect(() => {
    const limit = parseInt(process.env.NEXT_PUBLIC_STACK_TRACE_LIMIT ?? '', 10);
    if (limit > 0 && 'stackTraceLimit' in Error) {
      (Error as unknown as { stackTraceLimit: number }).stackTraceLimit = limit;
    }
  }, []);

  // If it's server side and not in preview mode, use build endpoint which should be configured to GraphCDN
  // If it's client side, or in preview mode, use the direct endpoint
  const endpoint =
    typeof window === 'undefined' && !pageProps.preview
      ? NEXT_PUBLIC_GRAPHQL_BUILD_ENDPOINT
      : NEXT_PUBLIC_GRAPHQL_ENDPOINT;

  const apolloClient = useApollo(pageProps.initialApolloState, {
    key: typeof window === 'undefined' ? 'SERVER' : 'CLIENT',
    endpoint,
    mutationEndpoint: NEXT_PUBLIC_GRAPHQL_MUTATION_ENDPOINT,
    handlers: {
      logoutHandler: logoutUser,
    },
    origin: NEXT_PUBLIC_APOLLO_ORIGIN,
    preview: pageProps.preview,
  });

  // keep track of navigation events for performance profiling
  const router = useRouter();
  useEffect(() => {
    const handleRouteChange = () => {
      performance.mark('navEvent');
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router]);

  const { key: pageKey, ...componentProps } = pageProps;

  return (
    <>
      <Head>
        {/* Viewport configuration for iOS safe areas and dynamic UI */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover, maximum-scale=5"
        />
      </Head>
      <style jsx global>{`
        :root {
          --font-open-sans: ${openSans.style.fontFamily};
          --font-abril-fatface: ${abrilFatface.style.fontFamily};
        }
      `}</style>
      <Script id="autofill-callback-handler" strategy="afterInteractive">
        {`window._AutofillCallbackHandler = window._AutofillCallbackHandler || function() {};`}
      </Script>
      <SnapchatScript />
      <MetaPixelScript />
      <OpenAIAdsScript />
      <PodscribeScript />
      <AdalyserScript />
      <ErrorBoundary>
        <ApolloProvider client={apolloClient}>
          <WebTheme>
            <GtmScript />
            <HotjarScript />
            <HubSpotConsent />
            <AlertProvider
              template={AlertTemplate}
              position={positions.BOTTOM_RIGHT}
              {...alertOptions}>
              <ModalProvider>
                <AuthProvider>
                  <AnalyticsProvider>
                    <AnalyticsAuthBridge />
                    <GrowthBookProvider
                      apiHost={process.env.NEXT_PUBLIC_GROWTHBOOK_API_HOST}
                      clientKey={process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY}
                      siteId={globalState?.siteConfig?.siteId}>
                      <AmplitudeExperimentScript />
                      <SignupAttributionProvider>
                        <NewRelicErrorContextProvider pageProps={pageProps}>
                          <BrazeInitializer />
                          <SnapchatPixelUpdater />
                          <MetaPixelUpdater />
                          <Component key={pageKey} {...componentProps} globalData={globalState} />
                          <GlobalConsentBanner />
                        </NewRelicErrorContextProvider>
                      </SignupAttributionProvider>
                    </GrowthBookProvider>
                  </AnalyticsProvider>
                </AuthProvider>
              </ModalProvider>
            </AlertProvider>
          </WebTheme>
        </ApolloProvider>
      </ErrorBoundary>
    </>
  );
};

export default App;
